import React, { useRef, useState, useEffect } from "react";
import { Button } from "../ui/button";

import FullscreenViewDialog from "../fullscreen-view-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Camera, Download, Ellipsis, FileText, RefreshCcw, Trash, Upload, Video } from 'lucide-react';
import { showErrorToast, showSuccessToast } from "../toast/toast";
import { backendApiUrl } from "@/lib/config";
import oktaService from "@/services/OktaService";
import { processFileForUpload } from "@/utils/imageCompression";

interface MediaItem {
  id: number;
  url: string;
  contentType: 'image' | 'video' | 'pdf';
  type: string;
  palletId: number;
  file?: File;
  localUrl?: string; // Added for cached URLs
  isLoading?: boolean; // Loading state for images
  hasError?: boolean; // Add hasError property to fix TypeScript error
  errorMessage?: string;
  originalFilename?: string;
  processingCompress?: boolean; // Added to show compression state
}

interface UploadHandlerProps {
  palletId: number;
  category: string;
  mediaItems: MediaItem[];
  onAddMedia: (newItem: MediaItem) => void;
  onDeleteMedia: (mediaId: number) => void;
  projectId?: number; // Add optional projectId
  refetch: () => void;
}

const UploadHandler: React.FC<UploadHandlerProps> = ({ palletId, category, mediaItems, onAddMedia, onDeleteMedia, projectId, refetch }) => {
  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const cameraCaptureRef = useRef<HTMLInputElement | null>(null);
  const videoCaptureRef = useRef<HTMLInputElement | null>(null);
  const [localMediaItems, setLocalMediaItems] = useState<MediaItem[]>([]);
  const [uploading, setUploading] = useState(false);

  // We use a cache for images to avoid CORS issues
  const imageBlobCache = useRef<Map<string, string>>(new Map());

  // Debug settings - can be turned on in development if needed
  const DEBUG_IMAGE_LOADING = false; // Set to true for detailed logs
  const FORCE_RELOAD_IMAGES = false; // Set to true to bypass cache and reload all images

  useEffect(() => {
    // Initialize local media items with loading state
    const initialItems = mediaItems.map(item => ({
      ...item,
      isLoading: !item.file && !item.url.startsWith('blob:'),
      localUrl: item.file ? URL.createObjectURL(item.file) : item.url.startsWith('blob:') ? item.url : undefined
    }));

    setLocalMediaItems(initialItems);

    // Preload images for items that need it
    initialItems.forEach(item => {
      if (item.isLoading || (FORCE_RELOAD_IMAGES && !item.file)) {
        preloadImage(item);
      }
    });

    // Clean up any object URLs we created
    return () => {
      localMediaItems.forEach(item => {
        if (item.localUrl && item.localUrl.startsWith('blob:')) {
          URL.revokeObjectURL(item.localUrl);
        }
      });
    };
  }, [mediaItems]);

  // Function to preload an image and create a local blob URL
  const preloadImage = async (item: MediaItem) => {
    // Max number of retries
    const MAX_RETRIES = 2;
    let retryCount = 0;
    let lastError: any = null;

    // Try loading the image with retries
    while (retryCount <= MAX_RETRIES) {
      try {
        // Check if we already have this cached (unless we're forcing a reload)
        if (!FORCE_RELOAD_IMAGES && imageBlobCache.current.has(item.url)) {
          if (DEBUG_IMAGE_LOADING) {
            console.log(`Using cached image for: ${item.url}`);
          }
          updateMediaItemLocalUrl(item.id, imageBlobCache.current.get(item.url) as string);
          return;
        }

        // Get the URL that points to our backend
        const fullUrl = getFullMediaUrl(item.url);

        if (DEBUG_IMAGE_LOADING || retryCount > 0) {
          console.log(`Preloading image from: ${fullUrl}${retryCount > 0 ? ` (retry ${retryCount}/${MAX_RETRIES})` : ''}`);
        }

        // Get Okta token for authorization
        let headers: Record<string, string> = {};
        try {
          const oktaUser = await oktaService.getUser();
          if (oktaUser.access_token) {
            headers = {
              'Authorization': `Bearer ${oktaUser.access_token}`
            };
            if (DEBUG_IMAGE_LOADING) {
              console.log('Added authorization header for image fetch');
            }
          }
        } catch (tokenError) {
          console.warn('Could not get Okta token for image fetch:', tokenError);
          // Continue without auth header - might still work for public resources
        }

        const response = await fetch(fullUrl, {
          headers,
          credentials: 'include', // Include cookies if needed
          cache: FORCE_RELOAD_IMAGES ? 'no-store' : 'default' // Don't use cached responses when forcing reload
        });

        if (!response.ok) {
          // For 401/403 errors, we might need a new token, so retry
          if (response.status === 401 || response.status === 403) {
            throw new Error(`Authentication error: ${response.status} ${response.statusText}`);
          }

          throw new Error(`Failed to preload image: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        // Cache this URL for future use
        imageBlobCache.current.set(item.url, blobUrl);

        // Update the item in our local state
        updateMediaItemLocalUrl(item.id, blobUrl);

        if (DEBUG_IMAGE_LOADING) {
          console.log(`Successfully loaded image: ${item.url}`);
        }

        // Success, exit the retry loop
        return;
      } catch (error) {
        lastError = error;
        console.error(`Error preloading image (attempt ${retryCount + 1}/${MAX_RETRIES + 1}):`, error);

        if (retryCount < MAX_RETRIES) {
          // Exponential backoff delay between retries (300ms, then 900ms)
          const delay = 300 * Math.pow(3, retryCount);
          console.log(`Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          retryCount++;
        } else {
          // All retries failed
          break;
        }
      }
    }

    // All attempts failed
    console.error(`All ${MAX_RETRIES + 1} attempts to load image failed`, lastError);
    updateMediaItemLoadingState(item.id, false, true);
  };

  // Helper to update a specific media item's local URL
  const updateMediaItemLocalUrl = (id: number, localUrl: string) => {
    setLocalMediaItems(prev =>
      prev.map(item =>
        item.id === id
          ? { ...item, localUrl, isLoading: false, hasError: false }
          : item
      )
    );
  };

  // Helper to update a specific media item's loading state
  const updateMediaItemLoadingState = (id: number, isLoading: boolean, hasError: boolean = false) => {
    setLocalMediaItems(prev =>
      prev.map(item =>
        item.id === id
          ? { ...item, isLoading, hasError }
          : item
      )
    );
  };

  // New function to upload file to server
  const uploadToServer = async (file: File): Promise<MediaItem | null> => {
    try {
      // Different handling based on file type
      const isImage = file.type.startsWith('image/') && file.type !== 'image/gif';
      const isPdf = file.type === 'application/pdf';

      // For non-image files, we still enforce the 4MB limit
      if (!isImage) {
        const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4MB (increased from 2MB)
        if (file.size > MAX_FILE_SIZE) {
          throw new Error(`File size exceeds 4MB limit: ${file.name}`);
        }
      }

      setUploading(true);

      // Process the file (will compress images if needed)
      let processedFile = file;
      if (isImage) {
        processedFile = await processFileForUpload(file, 4 * 1024 * 1024); // 4MB limit for images (increased from 3MB)
      }

      // Check if the processed file is still too large for API limit (5MB)
      const API_MAX_SIZE = 5 * 1024 * 1024; // 5MB absolute limit
      if (processedFile.size > API_MAX_SIZE) {
        throw new Error(`File is too large even after compression (${(processedFile.size / (1024 * 1024)).toFixed(2)}MB). Maximum allowed size is 5MB.`);
      }

      // Get Okta token for authorization
      const oktaUser = await oktaService.getUser();
      if (!oktaUser || !oktaUser.access_token) {
        throw new Error('Not authenticated. Please log in again.');
      }

      // Create form data
      const formData = new FormData();
      formData.append('media', processedFile);
      formData.append('palletId', palletId.toString());
      formData.append('imageType', category);

      // If this is a PDF, we need to add fileType to make the backend allow it
      if (isPdf) {
        formData.append('fileType', 'unit-load-images');
        formData.append('subCategory', 'specNumber'); // Default subcategory for PDFs
      }

      // Send the file to the server
      const response = await fetch(`${backendApiUrl}/media/upload-file`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${oktaUser.access_token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        let errorMessage = 'Failed to upload file';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // If we can't parse JSON, use the status text
          errorMessage = `Upload failed: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Create a MediaItem from the response
      const mediaItem: MediaItem = {
        id: data.mediaId, // Server-generated ID
        url: data.url,
        contentType: getContentTypeFromFile(file),
        type: category,
        palletId: palletId,
        originalFilename: file.name,
        isLoading: false,
        hasError: false
      };

      // Now save the media record to the database if we don't already have a mediaId
      // This is important for uploads that aren't unit-load-images which don't auto-create DB records
      if (!data.mediaId && !data.created) {
        try {
          // Create the media record in the database using the /media/upload endpoint
          const createResponse = await fetch(`${backendApiUrl}/media/upload`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${oktaUser.access_token}`,
            },
            body: JSON.stringify({
              projectId: (await getProjectId()) || 0, // Get project ID from the URL or redux store
              mediaItems: [{
                palletId: palletId,
                url: data.url,
                contentType: getContentTypeFromFile(file),
                type: category,
                originalFilename: file.name,
                fileSize: file.size,
                metadata: {
                  blobName: data.blobName,
                  uploadedAt: new Date().toISOString()
                }
              }]
            }),
            credentials: 'include',
          });

          if (!createResponse.ok) {
            console.warn('Failed to create database record for uploaded file, but file was uploaded successfully');
            // Continue anyway since the file was uploaded
          } else {
            // Get the created media record
            const createData = await createResponse.json();
            if (createData && createData.length > 0) {
              // Update the media ID from the created record
              mediaItem.id = createData[0].id;
            }
          }
          refetch()
        } catch (dbError) {
          console.warn('Error creating database record:', dbError);
          // Continue since the file was successfully uploaded
        }
      }

      // Show success message
      showSuccessToast(`File uploaded successfully`);

      return mediaItem;
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown upload error';

      showErrorToast(errorMessage);
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Helper function to get the project ID - use the provided projectId first
  const getProjectId = async (): Promise<number | null> => {
    // If projectId was provided directly, use it
    if (projectId) {
      return projectId;
    }

    try {
      // Otherwise try to get from URL first
      const urlParams = new URLSearchParams(window.location.search);
      const projectIdParam = urlParams.get('projectId');
      if (projectIdParam) {
        return parseInt(projectIdParam, 10);
      }

      // Otherwise try to get from the page URL path
      const pathParts = window.location.pathname.split('/');
      for (let i = 0; i < pathParts.length; i++) {
        if (pathParts[i] === 'projects' && i + 1 < pathParts.length) {
          const projectIdFromPath = parseInt(pathParts[i + 1], 10);
          if (!isNaN(projectIdFromPath)) {
            return projectIdFromPath;
          }
        }
      }

      // If we still don't have it, see if there's a state variable or data attribute
      const projectIdElement = document.querySelector('[data-project-id]');
      if (projectIdElement) {
        const projectId = projectIdElement.getAttribute('data-project-id');
        if (projectId) {
          return parseInt(projectId, 10);
        }
      }

      return null;
    } catch (e) {
      console.error('Error getting project ID:', e);
      return null;
    }
  };

  // Helper to get content type from file
  const getContentTypeFromFile = (file: File): MediaItem['contentType'] => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    if (file.type === 'application/pdf') return 'pdf';
    return 'image'; // Default fallback
  };

  const handleFileUpload = () => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
    }
  };

  const handleCameraCapture = () => {
    if (cameraCaptureRef.current) {
      cameraCaptureRef.current.click();
    }
  };

  const handleVideoCapture = () => {
    if (videoCaptureRef.current) {
      videoCaptureRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Create a list to track all processing promises
    const processingPromises = [];

    // Process each file
    for (const file of Array.from(files)) {
      // Create a temporary item for UI with processing state
      const blobUrl = URL.createObjectURL(file);
      const tempItem: MediaItem = {
        id: Date.now() + Math.floor(Math.random() * 1000), // Temporary unique ID
        url: blobUrl,
        localUrl: blobUrl,
        contentType: getContentTypeFromFile(file),
        type: category,
        palletId: palletId,
        file: file,
        isLoading: true,
        originalFilename: file.name,
        processingCompress: file.type.startsWith('image/') && file.type !== 'image/gif' && file.size > 3 * 1024 * 1024
      };

      // Add to local state
      setLocalMediaItems(prev => [...prev, tempItem]);

      // Start processing
      const processPromise = (async () => {
        try {
          // Start upload (handles compression inside)
          const uploadedItem = await uploadToServer(file);

          if (uploadedItem) {
            // Update the temp item with the real data
            setLocalMediaItems(prev =>
              prev.map(item =>
                item.id === tempItem.id
                  ? { ...uploadedItem, localUrl: blobUrl, processingCompress: false }
                  : item
              )
            );

            // Notify parent
            onAddMedia(uploadedItem);
          } else {
            // Remove the temp item on failure
            setLocalMediaItems(prev =>
              prev.filter(item => item.id !== tempItem.id)
            );
          }
        } catch (error) {
          console.error('Error processing file:', error);
          // Update item to show error
          setLocalMediaItems(prev =>
            prev.map(item =>
              item.id === tempItem.id
                ? {
                  ...item,
                  isLoading: false,
                  processingCompress: false,
                  hasError: true,
                  errorMessage: error instanceof Error ? error.message : 'Unknown error'
                }
                : item
            )
          );
        }
      })();

      processingPromises.push(processPromise);
    }

    // Wait for all processing to complete
    await Promise.all(processingPromises);

    // Reset the input value to allow the same file to be selected again
    event.target.value = '';
  };

  const handleDelete = async (mediaId: number, isLocalFile: boolean) => {
    const confirmed = window.confirm('Are you sure you want to delete this media? This action cannot be undone.');
    if (!confirmed) return;

    try {
      // For local files, just update the state without making an API call
      if (isLocalFile) {
        setLocalMediaItems(prev => prev.filter(item => item.id !== mediaId));
        onDeleteMedia(mediaId);
        showSuccessToast('Media deleted successfully');
        return;
      }
      const oktaUser = await oktaService.getUser();

      // For uploaded files, make the API call
      const response = await fetch(`${backendApiUrl}/media/${mediaId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${oktaUser.access_token}`,
          // issite: 'true'
        }
      }
      );

      if (!response.ok) {
        throw new Error('Failed to delete media');
      }

      setLocalMediaItems(prev => prev.filter(item => item.id !== mediaId));
      onDeleteMedia(mediaId);
      refetch()
      showSuccessToast('Media deleted successfully');
    } catch (error) {
      console.error('Error deleting media:', error);
      showErrorToast('Failed to delete media');
    }
  };

  const handleDownload = async (url: string, contentType: string) => {
    try {
      console.log('handle downnlload cllicked', url)
      if (!url.startsWith('blob:')) {
        // Use our getFullMediaUrl function to ensure we have the correct URL
        const fullUrl = getFullMediaUrl(url);
        console.log(`Downloading from URL: ${fullUrl}`);

        // For proxy URLs, we need to add a download parameter
        const downloadUrl = fullUrl.includes('/api/media/image/')
          ? `${fullUrl}?download=true`
          : fullUrl;

        // Get Okta token for authorization
        const headers: Record<string, string> = {
          'Accept': 'application/json, image/*, video/*, application/octet-stream'
        };

        try {
          const oktaUser = await oktaService.getUser();
          if (oktaUser.access_token) {
            headers['Authorization'] = `Bearer ${oktaUser.access_token}`;
            console.log('Added authorization header for download');
          }
        } catch (tokenError) {
          console.warn('Could not get Okta token for download:', tokenError);
          // Continue without auth header - might still work for public resources
        }

        const response = await fetch(downloadUrl, {
          headers,
          credentials: 'include' // Include cookies if needed
        });

        if (!response.ok) {
          throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        const extension = contentType === 'image' ? '.jpg' : '.mp4';
        link.download = `${contentType}-${Date.now()}${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      showErrorToast('Failed to download file');
    }
  };

  // Function to get the full URL for an image or video
  const getFullMediaUrl = (url: string): string => {
    // If it's a blob URL or already absolute URL (starts with http/https), use as is
    if (url.startsWith('blob:') || url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // For API media image endpoints, we need special handling
    if (url.startsWith('/api/media/image/')) {
      // This is already a properly formatted proxy URL, we just need to make sure it points to the backend
      // backendApiUrl already includes /api, so we need to remove that from our url
      const blobName = url.split('/api/media/image/')[1];
      const fullUrl = `${backendApiUrl}/media/image/${blobName}`;
      console.log(`🖼️ Image proxy URL: ${url} -> ${fullUrl}`);
      return fullUrl;
    }

    // If it's a relative URL (like /api/something), prepend the API URL properly
    if (url.startsWith('/api/')) {
      const fullUrl = `${backendApiUrl}${url.substring(4)}`;
      console.log(`🖼️ API URL: ${url} -> ${fullUrl}`);
      return fullUrl;
    }

    // For other relative URLs
    if (url.startsWith('/')) {
      const fullUrl = `${backendApiUrl}${url}`;
      console.log(`🖼️ Relative URL: ${url} -> ${fullUrl}`);
      return fullUrl;
    }

    // Default case, just return the URL
    console.log(`🖼️ Using original URL: ${url}`);
    return url;
  };

  // A simple loading spinner component with optional compression indicator
  const LoadingSpinner = ({ isCompressing = false }: { isCompressing?: boolean }) => (
    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mb-4"></div>
      <div className="text-blue-500 text-center">
        {isCompressing ? 'Compressing image...' : 'Loading...'}
      </div>
      {isCompressing && (
        <div className="text-xs text-gray-500 mt-2 max-w-[80%] text-center">
          Large images are automatically compressed for optimal performance
        </div>
      )}
    </div>
  );

  // An error state component
  const ErrorState = ({ errorMessage }: { errorMessage?: string }) => (
    <div className="w-full h-full flex items-center justify-center bg-gray-100">
      <div className="text-red-500 text-center">
        <div className="text-5xl mb-2">⚠️</div>
        <div className="mb-1">Failed to load image</div>
        {errorMessage && (
          <div className="text-xs max-w-[90%] mx-auto">
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <div
        className={cn(
          "w-full mt-5 flex flex-center flex-wrap gap-4",
          localMediaItems.length > 0 && "justify-start"
        )}
      >
        {localMediaItems.length > 0 ? (
          localMediaItems.map((item) => (
            <div key={item.id} className="relative w-[300px] h-[300px] bg-white">
              {item.isLoading ? (
                <LoadingSpinner isCompressing={item.processingCompress} />
              ) : item.hasError ? (
                <ErrorState errorMessage={item.errorMessage} />
              ) : item.contentType === 'video' ? (
                <video
                  src={item.localUrl || getFullMediaUrl(item.url)}
                  className="w-full h-full object-cover"
                  controls
                />
              ) : item.contentType === 'pdf' ? (
                <div className="flex flex-col items-center justify-center h-full w-full bg-gray-100 p-4 rounded-md">
                  <FileText size={64} className="text-red-500 mb-2" />
                  <p className="text-sm font-semibold text-center truncate max-w-full">
                    {item.originalFilename || 'PDF Document'}
                  </p>
                </div>
              ) : (
                <img
                  src={item.localUrl || getFullMediaUrl(item.url)}
                  alt={`Uploaded Media ${item.id}`}
                  className="w-full h-full object-cover shadow-md"
                  onError={() => updateMediaItemLoadingState(item.id, false, true)}
                />
              )}
              <DropdownMenu>
                <DropdownMenuTrigger
                  className={cn(
                    "rounded-full text-white p-1 font-semibold text-sm flex capitalize items-center gap-2 outline-none absolute right-4 top-4 bg-gray-600"
                  )}
                >
                  <span>
                    <Ellipsis size={20} />
                  </span>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="rounded-lg p-0">
                  {!item.file && !item.url.startsWith('blob:') && (
                    <>
                      <DropdownMenuItem
                        className="p-1"
                        onClick={() => handleDownload(item.url, item.contentType)}
                      >
                        <span>
                          <Download size={20} />
                        </span>
                        &nbsp; Download
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-border-style-1" />
                    </>
                  )}
                  <DropdownMenuItem
                    className="p-1"
                    onClick={handleFileUpload}
                  >
                    <span>
                      <RefreshCcw size={20} />
                    </span>
                    &nbsp; Replace
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-border-style-1" />
                  <FullscreenViewDialog
                    mediaUrl={item.localUrl || getFullMediaUrl(item.url)}
                    isVideo={item.contentType === 'video'}
                    isPdf={item.contentType === 'pdf'}
                  />
                  <DropdownMenuSeparator className="bg-border-style-1" />
                  <DropdownMenuItem
                    onClick={() => handleDelete(item.id, !!item.file)}
                    className="text-states-error p-1"
                  >
                    <span>
                      <Trash size={20} />
                    </span>{" "}
                    &nbsp; Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))
        ) : (
          <p className="font-semibold text-md">No media uploaded</p>
        )}
      </div>

      <div className="flex gap-4 mt-5">
        <Button
          className="bg-white flex-1 flex-center justify-center text-[#64748B] relative border-dashed border-2 hover:bg-white"
          onClick={handleFileUpload}
          disabled={uploading}
        >
          {uploading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
              <span>Uploading...</span>
            </>
          ) : (
            <>
              <Upload className="mr-2" size={20} />
              <span>Browse Files to upload</span>
            </>
          )}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              className="bg-white flex-1 flex-center justify-center text-[#64748B] relative border-dashed border-2 hover:bg-white"
              disabled={uploading}
            >
              <Camera className="mr-2" size={20} />
              <span>Capture from Camera</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={handleCameraCapture} disabled={uploading}>
              <Camera className="mr-2" size={16} />
              <span>Take Photo</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleVideoCapture} disabled={uploading}>
              <Video className="mr-2" size={16} />
              <span>Record Video</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <input
        type="file"
        ref={inputFileRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
        accept="image/*,video/*,application/pdf"
        multiple
        aria-label="File upload input"
      />
      <input
        type="file"
        ref={cameraCaptureRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
        accept="image/*"
        capture="environment"
        aria-label="Camera capture input"
      />
      <input
        type="file"
        ref={videoCaptureRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
        accept="video/*"
        capture="environment"
        aria-label="Video capture input"
      />
    </div>
  );
};

export default UploadHandler;
